import { SlimTag } from '../tagsApi';
import { httpClient } from '@texas/api/httpClient/httpClient';
import { Composition, Color } from '../compositionApi';
import { SimpleProductApproval } from '../productApprovalsApi';
import { MatrixAxis } from './compositionGroupMatrixApi';
import { SampleState, SampleTransportRequest } from '../samplesApi';
import {
  CompositionGroupHistoryEvent,
  EntityHistory,
} from '@texas/api/historyTypes';
import { SampleLifecycle } from '@texas/components/shared/samples/types';
import { AutoUpdateRequest, AutoUpdateResponse } from '../autoUpdateTypes';

interface CreateCompositionGroupRequest {
  variantId: number | undefined;
  basedOnId?: number;
  supplierId?: number;
  branchId?: number;
  cellX?: number;
  cellY?: number;
  template?: boolean;
}

export interface CopyConfig {
  optionName?: string;
  copySuppliersAndPurchaseInfo: boolean;
  copySalesPrices: boolean;
  copyGarpData: boolean;
}

interface CopyCompositionGroupRequest extends CreateCompositionGroupRequest {
  replaceCompositionGroupId: number | undefined;
  copyConfig?: CopyConfig;
}

export interface UpdateCompositionGroupRequest {
  note: string;
}

export interface CompositionGroupSample {
  id: number;
  sampleInititatedDate: string;
  sampleReadyDate: string | null;
  state: SampleState;
  transport: SampleTransportRequest | null;
  inPossessionOfUserId: number | null;
  inPossessionOfContactId: number | null;
  receivedFromSupplierUnknownShipment: string | null;
  isMain: boolean;
  lifecycle: SampleLifecycle;
}

export interface CompositionGroup {
  articleId: number;
  articleItemCode: string;
  id: number;
  variantId: number;
  linkedVariantId: number | null;
  linkedVariantName: string | null;
  cellX: number | null;
  matrixNodeXValue: string | null;
  cellY: number | null;
  matrixNodeYValue: string | null;
  colorAxis: MatrixAxis | null;
  supplierId: number | null;
  branchId: number | null;
  branchIdentifier: string | null;
  note: string | null;
  version: number;
  isControllerVersion: boolean;
  exists: boolean;
  compositions: Composition[];
  productApprovals: SimpleProductApproval[] | null;
  controlledGroupsProductApprovals: SimpleProductApproval[] | null;
  anyProductApproval: boolean;
  completedDate: string | null;
  mainProductApproval: SimpleProductApproval | null;
  archived: string | null;
  fileId: number | null;
  fileIdentifier: string | null;
  artworkFileId: number | null;
  artworkFileIdentifier: string | null;
  folderId: number;
  hasAnyBasedOn: boolean;
  hasArchivedCompositions: boolean;
  basedOnGroupId: number | null;
  controlledById: number | null;
  isReady: boolean;
  supplierName: string;
  supplierTags?: SlimTag[];
  basedOnGroupVersion: number | null;
  basedOnGroupName: string | null;
  readOnly: boolean;
  followers: { name: string; branch: string; supplierId: number }[] | null;
  compositionGroupPurchaseInfoId: number | null;
  inquiryId: number | null;
  formattedInquiryNumber: string | null;
  template: boolean;
  patchCount: number;
  controlledGroupIds: number[];
}

export interface CompositionGroupId {
  id: number;
  variantId: number;
}

export enum MatchMode {
  Exact = 0,
  Fuzzy = 1,
}

interface LocateCompositionGroupParams {
  variantId: number | undefined;
  cellX?: number;
  cellY?: number;
  supplierId?: number;
  branchId?: number;
  template?: boolean;
  matchMode?: MatchMode;
}

interface VersionsOverviewFilters {
  variantId: number;
  supplierIds?: number[];
  optionIds?: number[];
  branchIds?: number[];
  cellX?: number;
  cellY?: number;
}

export interface SupplierInquiryDto {
  id: number;
  compositionGroupPurchaseInfoId: number;
  basedOnId: number | null;
  created: string;
  archived: string | null;
  exchangeRate: number;
  currencyId: number;
  handlingFeeId: number | null;
  freightCostId: number | null;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  createdByUserId: number;
  tierPrices: Array<{
    id: number;
    targetTierId: number;
    quantity: number;
    price: number | null;
  }>;
}

interface SearchCompositionGroupsParams {
  variantId: number;
  branchIds: number[];
  supplierIds: number[];
}

export interface CompositionCell {
  x: number;
  y: number;
  colors: Color[];
  versionsCount: number;
  optionsCount: number;
}

interface GetCompositionGroupParams {
  includeArchived: boolean;
}

export interface CreateSamplesData {
  groupId: number;
  supplierNote: string;
  externalSupplierItemCode: string;
  supplierContactId: number;
  customerContactId: number;
}

export interface CreateSamplesRequest {
  readyDate: Date | null;
  data: CreateSamplesData[];
}

interface ArchiveStatusCompositionGroup {
  pendingPasToCancel: string[];
  paActiveSamples: { [key: string]: number };
  acceptedPas: string[];
}

export interface AutoUpdateCompositionGroupConfig {
  templateProductGroupId: number | null;
}

export interface CompositionGroupConfig {
  compositionGroupId: number;
  templateProductGroupId: number | null;
  isVdcTemplate: boolean;
  standaloneFolderId: number | null;
}

export const EMPTY_AUTO_UPDATE_COMPOSITION_GROUP_CONFIG: AutoUpdateCompositionGroupConfig =
  {
    templateProductGroupId: null,
  };

export const compositionGroupApi = {
  addGroup: (data: CreateCompositionGroupRequest) =>
    httpClient.post<CompositionGroup>(`/compositiongroups`, {
      data,
    }),
  copyGroup: (from: number, data: CopyCompositionGroupRequest) =>
    httpClient.put<CompositionGroup>(`/compositiongroups/${from}/copy`, {
      data,
    }),
  setBasedOn: (id: number, basedOnId: number) =>
    httpClient.put<CompositionGroup>(
      `/compositiongroups/${id}/based-on/${basedOnId}`,
    ),
  updateGroup: (id: number, data: UpdateCompositionGroupRequest) =>
    httpClient.put<CompositionGroup>(`/compositiongroups/${id}`, { data }),
  updateFile: (id: number, fileId: number) =>
    httpClient.put(`/compositiongroups/${id}/file/${fileId}`),
  deleteFile: (id: number) =>
    httpClient.delete(`/compositiongroups/${id}/file`),
  updateArtworkFile: (id: number, fileId: number) =>
    httpClient.put(`/compositiongroups/${id}/artwork/${fileId}`),
  deleteArtworkFile: (id: number) =>
    httpClient.delete(`/compositiongroups/${id}/artwork`),
  getGroup: (id: number, params?: GetCompositionGroupParams) =>
    httpClient.get<CompositionGroup>(`/compositiongroups/${id}`, { params }),
  getArchivedGroups: (params: LocateCompositionGroupParams) =>
    httpClient.get<CompositionGroupId[]>(`compositiongroups/archived`, {
      params,
    }),
  searchGroup: (params: SearchCompositionGroupsParams) =>
    httpClient.get<CompositionGroup[]>(`/compositiongroups/search`, {
      params,
    }),
  versionsOverview: (params: VersionsOverviewFilters) =>
    httpClient.post<CompositionGroup[]>(
      `/compositiongroups/overview/versions`,
      {
        data: params,
      },
    ),
  optionsOverview: (params: { variantId: number }) =>
    httpClient.get<CompositionGroup[]>(`/compositiongroups/overview/options`, {
      params,
    }),
  templatesOverview: (params?: { isReady: boolean }) =>
    httpClient.get<CompositionGroup[]>(
      `/compositiongroups/overview/templates`,
      {
        params,
      },
    ),
  archive: (id: number) =>
    httpClient.put<CompositionGroup>(`compositiongroups/${id}/archive`),
  archiveStatus: (id: number) =>
    httpClient.get<ArchiveStatusCompositionGroup>(
      `compositiongroups/${id}/archive/status`,
    ),
  restore: (id: number) =>
    httpClient.put<CompositionGroup>(`compositiongroups/${id}/restore`),
  markReady: (id: number) => httpClient.put(`compositiongroups/${id}/ready`),
  markNotReady: (id: number) =>
    httpClient.put(`compositiongroups/${id}/not-ready`),
  createSamples: (data: CreateSamplesRequest) =>
    httpClient.put(`compositiongroups/samples`, { data }),
  getHistory: (id: number) =>
    httpClient.get<EntityHistory<CompositionGroupHistoryEvent>[]>(
      `compositiongroups/${id}/history`,
    ),
  getConfig: (compositionGroupId: number) =>
    httpClient.get<CompositionGroupConfig>(
      `/compositiongroups/${compositionGroupId}/config`,
    ),
  autoUpdateConfig: (
    request: AutoUpdateRequest<AutoUpdateCompositionGroupConfig>,
    compositionGroupId: number,
  ) =>
    httpClient.put<AutoUpdateResponse<CompositionGroupConfig>>(
      `/compositiongroups/${compositionGroupId}/config`,
      { data: request },
    ),
};
